// src/components/utils/printEmpresaInformePdf.js

function escapeHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

const MESES_ES = [
  "enero","febrero","marzo","abril","mayo","junio",
  "julio","agosto","septiembre","octubre","noviembre","diciembre",
];

function fmtDateLargo(v) {
  if (!v) return "—";
  const str = String(v);
  let datePart = str;
  let timePart = null;
  if (str.includes("T")) [datePart, timePart] = str.split("T");
  else if (/\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(str)) [datePart, timePart] = str.split(" ");
  if (!datePart.includes("-")) return str;
  const [y, m, d] = datePart.split("-");
  const mes = MESES_ES[parseInt(m, 10) - 1];
  if (!mes) return str;
  const dia = String(parseInt(d, 10)).padStart(2, "0");
  let base = `${dia} de ${mes} de ${y}`;
  if (timePart) {
    const hhmm = timePart.slice(0, 5);
    if (hhmm && hhmm !== "00:00") base += ` a las ${hhmm} horas`;
  }
  return base;
}

function fmtDateISO(s) {
  if (!s) return "—";
  const [y, m, d] = String(s).slice(0, 10).split("-");
  if (!y || !m || !d) return "—";
  return `${d}/${m}/${y}`;
}

function fmtMoney(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("es-MX", { style: "currency", currency: "MXN" });
}

function toTitleCase(s) {
  if (!s) return "—";
  return String(s)
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/(^|[\s.,/-])([a-záéíóúñü0-9])/g, (m, sep, c) => sep + c.toUpperCase());
}

// Se imprime vía window.print() del navegador dentro de un iframe propio
// (ver printHtmlAsPdf), así que este documento vive aislado del resto de la
// app y puede usar selectores normales — el navegador pagina el texto real,
// sin los huecos en blanco que deja recortar una captura de pantalla.
const css = `
  @page{ size:A4; margin:0; }
  :root{ --ink:#111827; --muted:#6b7280; --line:#d6dae1; --head:#eceff4; --rowAlt:#f7f8fb; }
  *{ box-sizing:border-box; }
  html,body{ height:100%; }
  body{
    margin:0;
    font-family:Arial,Helvetica,sans-serif;
    color:var(--ink);
    background:#eef1f5;
  }
  .page{
    width:210mm;
    min-height:297mm;
    margin:0 auto;
    background:#fff;
    padding:4mm 20mm 20mm 20mm;
    position:relative;
  }
  .letterhead{
    display:grid;
    grid-template-columns:120px 1fr 120px;
    align-items:center;
    gap:12px;
    margin-bottom:6px;
  }
  .lh-spacer{ width:120px; height:60px; }
  .lh-title{ text-align:center; line-height:1.15; }
  .lh-name{ font-weight:700; letter-spacing:.7px; font-size:20px; }
  .lh-sub{ margin-top:5px; font-weight:500; font-size:18px; }
  .lh-rule{ border-top:2px solid #111; margin:6px 0; }
  .lh-rule2{ border-top:1px solid #111; margin:6px 0; }
  .lh-contacts{
    display:grid; grid-template-columns:1fr 1fr; gap:18px;
    font-size:11px; line-height:1.35; color:#111827;
  }
  .lh-col a{ color:#1d4ed8; text-decoration:underline; }

  h1{
    margin:14mm 0 8mm 0;
    font-size:18px;
    font-weight:700;
    text-align:center;
    color:#1f2937;
  }

  .periodBox{
    margin:14px 0 18px 0;
    border:1px solid var(--line);
    background:#f7f8fb;
    padding:12px 14px;
    border-radius:10px;
    font-size:13px;
    color:#111827;
  }
  .periodRow{ display:flex; gap:16px; margin-bottom:5px; flex-wrap:wrap; justify-content:space-between; }
  .periodRow b{ font-weight:700; }

  hr.sep{ border:0; border-top:1px solid var(--line); margin:14px 0 16px 0; }

  .kpis{ display:grid; grid-template-columns:repeat(5,1fr); gap:10px; margin:0 0 18px; }
  .kpi{ border:1px solid var(--line); border-radius:8px; padding:10px 12px; background:#f7f8fb; }
  .kpi .lbl{ font-size:9px; font-weight:700; letter-spacing:.05em; color:var(--muted); text-transform:uppercase; margin-bottom:6px; }
  .kpi .val{ font-size:20px; font-weight:800; color:#111827; }

  .sectionTitle{
    font-size:13px; font-weight:700; color:#111827;
    margin:10mm 0 8px 0;
    display:flex; align-items:center; gap:8px;
  }
  .sectionTitle .count{
    background:#eef1f8; color:#031636; font-size:11px; font-weight:700;
    padding:1px 7px; border-radius:10px;
  }
  .moduloTag{
    display:inline-block; font-size:10px; font-weight:700; letter-spacing:.04em;
    text-transform:uppercase; background:#e0f2f1; color:#00695c;
    padding:2px 8px; border-radius:4px; margin:10px 0 6px;
  }

  table{ width:100%; border-collapse:collapse; border:1px solid var(--line); margin-top:6px; }
  thead th{
    background:var(--head); border-bottom:1px solid var(--line);
    font-size:10.5px; text-align:left; padding:7px 8px; color:#111827; font-weight:700;
  }
  thead{ display:table-header-group; }
  tbody td{ font-size:10.5px; padding:7px 8px; border-top:1px solid var(--line); vertical-align:top; color:#111827; }
  tbody tr:nth-child(even) td{ background:var(--rowAlt); }
  tr{ page-break-inside:avoid; break-inside:avoid; }
  .moduloTag{ page-break-after:avoid; break-after:avoid; }
  .right{ text-align:right; }
  .muted{ color:var(--muted); font-style:italic; }

  @media print{
    body{ background:#fff; }
    .page{ margin:0; width:auto; min-height:auto; }
  }
`;

export const SECTION_COLUMNS = {
  audiencias: [
    { key:"fecha_audiencia", label:"FECHA" },
    { key:"ne",               label:"Nº EXPEDIENTE" },
    { key:"estado",           label:"Nº AUDIENCIA" },
    { key:"tipo_audiencia",   label:"RESULTADO" },
    { key:"autoridad",        label:"AUTORIDAD" },
    { key:"abogado",          label:"RESPONSABLE" },
  ],
  asuntos: [
    { key:"ne",                 label:"Nº EXPEDIENTE" },
    { key:"modulo",             label:"MÓDULO" },
    { key:"trabajador",         label:"TRABAJADOR" },
    { key:"abogado",            label:"RESPONSABLE" },
    { key:"autoridad",          label:"AUTORIDAD" },
    { key:"audiencias_periodo", label:"AUD." },
    { key:"fecha",              label:"ÚLTIMA ACTUACIÓN" },
    { key:"estado",             label:"ESTADO" },
  ],
  convenios: [
    { key:"ne",          label:"Nº EXPEDIENTE" },
    { key:"fecha",       label:"FECHA CONVENIO" },
    { key:"trabajador",  label:"TRABAJADOR" },
    { key:"monto",       label:"MONTO" },
    { key:"pagos",       label:"PAGOS" },
    { key:"estado",      label:"ESTADO" },
  ],
};

const SECTION_TITLES = {
  audiencias: "Audiencias",
  asuntos:    "Asuntos atendidos",
  convenios:  "Convenios y cumplimientos",
};

function buildKpisHtml(kpis) {
  if (!kpis?.length) return "";
  return `
    <div class="kpis">
      ${kpis.map((k) => `
        <div class="kpi">
          <div class="lbl">${escapeHtml(k.lbl)}</div>
          <div class="val"${k.color ? ` style="color:${escapeHtml(k.color)}"` : ""}>${escapeHtml(k.val)}</div>
        </div>
      `).join("")}
    </div>
  `;
}

function fmtCell(key, row) {
  const v = row[key];
  switch (key) {
    case "fecha_audiencia":
    case "fecha":
      return fmtDateLargo(v);
    case "monto":
      return fmtMoney(v);
    case "tipo_audiencia":
    case "autoridad":
    case "abogado":
    case "trabajador":
    case "estado":
      return v ? toTitleCase(v) : "—";
    case "audiencias_periodo":
      return v ?? 0;
    default:
      return v || "—";
  }
}

function filterCols(cols, visibleKeys) {
  if (!visibleKeys) return cols;
  return cols.filter((c) => visibleKeys.has(c.key));
}

function buildRowsTableHtml(cols, rows) {
  if (!rows?.length) return `<p class="muted">Sin registros en el periodo</p>`;
  return `
    <table>
      <thead>
        <tr>${cols.map((c) => `<th${c.key === "audiencias_periodo" || c.key === "monto" ? ' class="right"' : ""}>${escapeHtml(c.label)}</th>`).join("")}</tr>
      </thead>
      <tbody>
        ${rows.map((r) => `
          <tr>
            ${cols.map((c) => `<td${c.key === "audiencias_periodo" || c.key === "monto" ? ' class="right"' : ""}>${escapeHtml(fmtCell(c.key, r))}</td>`).join("")}
          </tr>
        `).join("")}
      </tbody>
    </table>
  `;
}

function buildAudienciasHtml(audiencias, visibleKeys) {
  const cols = filterCols(SECTION_COLUMNS.audiencias, visibleKeys);
  const grupos = Object.entries(
    (audiencias || []).reduce((acc, a) => {
      const m = a.modulo || "Centro de Conciliación";
      (acc[m] = acc[m] || []).push(a);
      return acc;
    }, {})
  );

  if (!grupos.length) {
    return `<div class="sectionTitle">Audiencias <span class="count">0</span></div><p class="muted">Sin audiencias en el periodo</p>`;
  }

  return `
    <div class="sectionTitle">Audiencias <span class="count">${audiencias.length}</span></div>
    ${grupos.map(([modulo, items]) => `
      <div class="moduloTag">${escapeHtml(modulo)} · ${items.length} audiencia${items.length === 1 ? "" : "s"}</div>
      ${buildRowsTableHtml(cols, items)}
    `).join("")}
  `;
}

function buildAsuntosHtml(asuntos, visibleKeys) {
  const cols = filterCols(SECTION_COLUMNS.asuntos, visibleKeys);
  if (!asuntos?.length) {
    return `<div class="sectionTitle">Asuntos atendidos <span class="count">0</span></div><p class="muted">Sin asuntos en el periodo</p>`;
  }
  return `
    <div class="sectionTitle">Asuntos atendidos <span class="count">${asuntos.length}</span></div>
    ${buildRowsTableHtml(cols, asuntos)}
  `;
}

function buildConveniosHtml(convenios, visibleKeys) {
  const cols = filterCols(SECTION_COLUMNS.convenios, visibleKeys);
  if (!convenios?.length) {
    return `<div class="sectionTitle">Convenios y cumplimientos <span class="count">0</span></div><p class="muted">Sin convenios en el periodo</p>`;
  }
  return `
    <div class="sectionTitle">Convenios y cumplimientos <span class="count">${convenios.length}</span></div>
    ${buildRowsTableHtml(cols, convenios)}
  `;
}

function buildLetterheadHtml() {
  return `
    <div class="letterhead">
      <div class="lh-spacer"></div>
      <div class="lh-title">
        <div class="lh-name">ONTIVEROS &amp; ASOCIADOS</div>
        <div class="lh-sub">ABOGADOS</div>
      </div>
      <div class="lh-spacer"></div>
    </div>

    <div class="lh-rule"></div>
    <div class="lh-rule2"></div>

    <div class="lh-contacts">
      <div class="lh-col">
        <div>Calle "H" y Ave. Marmoleros #1598 Altos</div>
        <div>Col. Industrial, C.P. 21010, Mexicali, Baja California</div>
        <div>Tel. 686 554 54 82</div>
        <div>Correo: <a href="mailto:recepcionmxl@ontiverosyasociados.com.mx">recepcionmxl@ontiverosyasociados.com.mx</a></div>
      </div>
      <div class="lh-col">
        <div>Calle 2 #200 Local 8, Plaza Gabriela, Comercial 83449</div>
        <div>San Luis Río Colorado, Sonora</div>
        <div>Tel. 653 534 14 02, 534 41 91</div>
        <div>Correo: <a href="mailto:victor@ontiverosyasociados.com.mx">victor@ontiverosyasociados.com.mx</a></div>
      </div>
    </div>

    <div class="lh-rule"></div>
  `;
}

function buildMetaBoxHtml({ empresa, fechaInicio, fechaFin, ciudadesLabel, responsablesLabel, createdAtLocal }) {
  return `
    <div class="periodBox">
      <div class="periodRow">
        <div><b>Empresa:</b> ${escapeHtml(empresa)}</div>
        <div><b>Fecha de elaboración:</b> ${escapeHtml(createdAtLocal)}</div>
      </div>
      <div class="periodRow">
        <div><b>Periodo:</b> ${escapeHtml(fmtDateISO(fechaInicio))} – ${escapeHtml(fmtDateISO(fechaFin))}</div>
      </div>
      <div class="periodRow">
        <div><b>Ciudades:</b> ${escapeHtml(ciudadesLabel || "—")}</div>
      </div>
      ${responsablesLabel ? `
      <div class="periodRow">
        <div><b>Responsables:</b> ${escapeHtml(responsablesLabel)}</div>
      </div>` : ""}
    </div>
  `;
}

function buildDocument(titleTag, bodyInner) {
  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<title>${escapeHtml(titleTag)}</title>
<style>${css}</style>
</head>
<body>
  <div class="page">
    ${bodyInner}
  </div>
  <script>
    window.onload = () => { setTimeout(() => window.print(), 50); };
  </script>
</body>
</html>`;
}

function buildEmpresaInformeHtml({
  empresa, fechaInicio, fechaFin, ciudadesLabel, responsablesLabel,
  kpis, audiencias, asuntos, convenios,
}) {
  const now = new Date();
  const createdAtLocal = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()}`;

  const bodyInner = `
    ${buildLetterheadHtml()}
    <h1>Informe de Actividad</h1>
    ${buildMetaBoxHtml({ empresa, fechaInicio, fechaFin, ciudadesLabel, responsablesLabel, createdAtLocal })}
    ${buildKpisHtml(kpis)}
    <hr class="sep" />
    ${buildAudienciasHtml(audiencias)}
    ${buildAsuntosHtml(asuntos)}
    ${buildConveniosHtml(convenios)}
  `;

  return buildDocument(`Informe de actividad - ${empresa}`, bodyInner);
}

// Imprime vía el diálogo nativo del navegador (iframe oculto + window.print()),
// igual que el resto de reportes del sistema: el navegador pagina texto real,
// así que las filas de las tablas nunca se cortan a la mitad ni dejan huecos
// en blanco entre páginas como pasaba con la captura de pantalla (html2canvas).
function printHtmlAsPdf(html) {
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.setAttribute("aria-hidden", "true");
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    throw new Error("No se pudo inicializar el iframe de impresión.");
  }

  doc.open();
  doc.write(html);
  doc.close();

  const cleanup = () => {
    setTimeout(() => {
      try { document.body.removeChild(iframe); } catch {}
    }, 400);
  };

  iframe.contentWindow?.addEventListener("afterprint", cleanup, { once: true });
  setTimeout(cleanup, 6000);
}

export function printEmpresaInformePdf({
  empresa, fechaInicio, fechaFin, ciudadesLabel, responsablesLabel,
  kpis, audiencias, asuntos, convenios,
}) {
  const html = buildEmpresaInformeHtml({
    empresa, fechaInicio, fechaFin, ciudadesLabel, responsablesLabel,
    kpis, audiencias, asuntos, convenios,
  });
  printHtmlAsPdf(html);
}

const SECTION_ORDER = ["audiencias", "asuntos", "convenios"];

// Imprime una o varias tablas (Audiencias / Asuntos atendidos / Convenios)
// en un solo documento, respetando el orden fijo y las columnas visibles que
// tenga configuradas cada una en ese momento.
export function printEmpresaSeccionesPdf({
  sections, empresa, fechaInicio, fechaFin, ciudadesLabel, responsablesLabel,
  dataBySection, visibleKeysBySection, kpis,
}) {
  const selected = SECTION_ORDER.filter((s) => (sections || []).includes(s));
  if (!selected.length) return;

  const now = new Date();
  const createdAtLocal = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()}`;
  const title = selected.length === 1 ? (SECTION_TITLES[selected[0]] || "Detalle") : "Informe de Actividad";

  const bodyHtml = selected.map((s) => {
    const rows = (dataBySection || {})[s] || [];
    const visibleKeys = (visibleKeysBySection || {})[s] ?? null;
    if (s === "audiencias") return buildAudienciasHtml(rows, visibleKeys);
    if (s === "asuntos") return buildAsuntosHtml(rows, visibleKeys);
    return buildConveniosHtml(rows, visibleKeys);
  }).join("");

  const bodyInner = `
    ${buildLetterheadHtml()}
    <h1>${escapeHtml(title)}</h1>
    ${buildMetaBoxHtml({ empresa, fechaInicio, fechaFin, ciudadesLabel, responsablesLabel, createdAtLocal })}
    ${buildKpisHtml(kpis)}
    <hr class="sep" />
    ${bodyHtml}
  `;

  const html = buildDocument(`${title} - ${empresa}`, bodyInner);
  printHtmlAsPdf(html);
}
